/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import usePluginUtils from "./usePluginUtils";
import { useBooking } from "./useBooking";
import { setPendingAccessService } from "../store/slices/bookingSlice";
import { servicesRequireMembership } from "../utils/membershipGrant";
import { isClassService } from "../utils/classes";

// A class booking is always exactly one class with its own provider.
export const useClassSelection = () => {
  const dispatch = useDispatch();
  const { isPluginEnabled } = usePluginUtils();
  const {
    selectedServices,
    setSelectedServices,
    setSelectedProvider,
    setSelectedCategories,
    setSelectedProducts,
    setSelectedTime,
    resetBookingRequest,
  } = useBooking();
  const selectedClass = isClassService(selectedServices[0]) ? selectedServices[0] : null;

  const selectClass = useCallback(
    (classItem, category, count = 1) => {
      const isSameClass = selectedClass?.id === classItem.id;
      const requiresMembership = isPluginEnabled("membership") && servicesRequireMembership([classItem]);

      if (requiresMembership && !isSameClass) {
        resetBookingRequest();
        dispatch(setPendingAccessService(classItem));
        return false;
      }

      const keepsTime = isSameClass && (selectedClass.group_booking_count || 1) === count;
      const extras = isSameClass
        ? {
            addons: selectedClass.addons,
            isAddonsInitialized: selectedClass.isAddonsInitialized,
            products: selectedClass.products,
            isProductsInitialized: selectedClass.isProductsInitialized,
          }
        : { addons: [], isAddonsInitialized: false, products: [], isProductsInitialized: false };

      setSelectedServices([{ ...classItem, ...extras, group_booking_count: count, can_combine: false }]);
      setSelectedProvider(classItem.provider);
      setSelectedCategories(category ? [category] : []);
      setSelectedProducts([]);
      if (!keepsTime) setSelectedTime(null);

      return true;
    },
    [
      selectedClass,
      isPluginEnabled,
      resetBookingRequest,
      setSelectedProvider,
      dispatch,
      setSelectedServices,
      setSelectedCategories,
      setSelectedProducts,
      setSelectedTime,
    ],
  );

  return { selectedClass, selectClass };
};
